import { message } from 'antd';
import { Credit, CreditStatus } from '@types';
import { Link, useRedirect } from 'react-admin';
import IconButton from '@mui/material/IconButton';
import { CheckCircleFilled, CopyOutlined, RightCircleFilled } from '@ant-design/icons';

import { InitialCard } from '@components';
import { centsFormatter } from '@utils';
import { CreditStatusField } from '@pages/credit/CreditStatusField';
import cls from './credit-card.module.css';

export const CreditCard = ({ credit }: {credit: Credit}) => {
  const redirect = useRedirect();

  const showCreditAmount = [
    CreditStatus.SIGNED,
    CreditStatus.DELAY,
    CreditStatus.RESTRUCTURE,
    CreditStatus.REPAID,
  ].includes(credit.status);

  const user = credit.user;

  const onAvatarClick = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    redirect(`/user/${user.id}`);
  };

  const copyData = async (e: any, val: string | null) => {
    e.stopPropagation();
    if (!val) return;
    await navigator.clipboard.writeText(val);
    message.info('The data is copied to clipboard.');
  };

  const getCrops = (): string => {
    if (credit.creditCrops.length) {
      const cropsArray = credit.creditCrops.map(c => c.financedCrop?.nameMx);
      return cropsArray.join(', ');
    }

    return '-';
  };

  return (
    <InitialCard
      left="Credit information"
      right={<RightCircleFilled
        onClick={() => redirect(`/credit/${credit.id}`)}
        className={cls.headerIcon}
      />}
      wrapperClass={cls.cardWrapper}
    >
      <div className={cls.box}>
        <div className={cls.info}>
          <div className={cls.mainInfo}>
            <div className={cls.icon}>
              <IconButton className={cls.contact} onClick={onAvatarClick}>
                <svg xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none">
                  <path d="M16 0C7.16571 0 0 7.16571 0 16C0 24.8343 7.16571 32 16 32C24.8343 32 32 24.8343 32 16C32 7.16571 24.8343 0 16 0ZM16 8C19.6 8 22.5257 10.9257 22.5257 14.5257C22.5257 18.1257 19.6 21.0514 16 21.0514C12.4 21.0514 9.47429 18.1257 9.47429 14.5257C9.47429 10.9257 12.4 8 16 8ZM16 29.9429C12.7543 29.9429 9.76 28.8229 7.38286 26.9486C8.22857 25.7371 9.62286 25.0057 11.1086 25.0057H20.8914C22.3771 25.0057 23.7714 25.7371 24.6171 26.9486C22.24 28.8229 19.2457 29.9429 16 29.9429Z"
                    style={{ fill: 'var(--primary-color)' }} />
                </svg>
              </IconButton>
            </div>
            <div className={cls.content}>
              <div className={cls.creditCode}>
                {credit.code}
                <CopyOutlined
                  className={cls.copyIcon}
                  onClick={(e) => copyData(e, credit.code)}
                />
              </div>
              <div className={cls.creditAccredited}>
                <Link to={`/user/${user.id}?initial`}
                  className={cls.userLink}
                  onClick={e => e.stopPropagation()}>
                  <div className={cls.userName}>
                    {credit.accreditedName || '(No company)'}
                  </div>
                </Link>
                <CopyOutlined
                  className={cls.copyIcon}
                  onClick={(e) => copyData(e, credit.accreditedName)}
                />
              </div>
              <div className={cls.userState}>
                State: {user.state?.name || '-'}
              </div>
            </div>
          </div>

          <div className={cls.balanceText}>
            {showCreditAmount ? 'Credit' : 'Requested'} Amount
          </div>

          <div className={cls.balanceValue}>
            <span className={cls.balanceValueText}>
              {centsFormatter.format(showCreditAmount
                ? (credit.isUsd ? credit.usd_bank_approval : credit.bankApproval)
                : (credit.isUsd ? credit.usd_requestAmount ?? 0 : credit.requestAmount),
              )}
              {credit.isUsd && ' USD'}
            </span>
          </div>

          <div className={cls.financedCrop}>
            <CheckCircleFilled className={cls.cropsIcon} />
            Financed crops: {getCrops()}
          </div>

          <div className={cls.status}>
            <CreditStatusField
              source="status"
              creditStatus={credit.status}
              className={cls.statusChip}
            />
          </div>
        </div>
      </div>
    </InitialCard>
  );
};
